import { createRenderer, getRenderer } from '@/core/renderer';
import { appClock } from '@/core/clock';
import { SceneManager } from '@/core/scene-manager';
import { InputManager } from '@/core/input-manager';
import { detectQuality, getQualityPreset } from '@/core/quality-scaler';
import { initLoaders } from '@/core/asset-loader';
import { updateProgress, hidePreloader } from '@/ui/preloader';
import { HUD } from '@/ui/hud';
import { ChatPanel } from '@/ui/chat-panel';
import { apiClient } from '@/lib/api-client';
import { authManager } from '@/auth/auth-manager';
import { showLoginScreen, hideLoginScreen } from '@/auth/login-screen';
import { dataManager } from '@/data/data-manager';
import { createExteriorScene, updateExteriorScene } from '@/scenes/exterior/exterior-scene';
import { createInteriorScene } from '@/scenes/interior/interior-scene';
import { WorkerManager } from '@/scenes/interior/worker-manager';
import {
  createHomepageScene, updateHomepageScene, getHomepageScene,
  getHomepageCamera, getHomepageDeskStations, getHomepageDeliverables,
  resizeHomepage, disposeHomepageScene,
} from '@/scenes/homepage/homepage-scene';
import { setupScrollAnimations, killScrollAnimations } from '@/scenes/homepage/homepage-animations';
import { createHomepageOverlay, removeHomepageOverlay } from '@/ui/homepage-overlay';
import { OnboardingOrchestrator } from '@/onboarding/onboarding-orchestrator';
import { OnboardingEffects, type StatusIconType } from '@/onboarding/onboarding-effects';
import { WorkspaceWhiteboard } from '@/ui/workspace-whiteboard';
import { TaskProgress } from '@/ui/task-queue';
import { DevLog } from '@/ui/dev-log';
import { ActivityLog } from '@/ui/activity-log';
import { extractDeskIndex } from '@/utils/worker-utils';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer, EffectPass, RenderPass, BloomEffect, SMAAEffect } from 'postprocessing';

/**
 * Hatchlings — Main Entry Point
 * Flow: AUTH CHECK → LOADING → HOMEPAGE → EXTERIOR → INTERIOR
 */

let sceneManager: SceneManager;
let inputManager: InputManager;
let hud: HUD;
let chatPanel: ChatPanel;
let workerManager: WorkerManager | null = null;
let onboardingOrchestrator: OnboardingOrchestrator | null = null;
let composer: EffectComposer | null = null;
let orbitControls: OrbitControls | null = null;
let taskEffects: OnboardingEffects | null = null;
let taskProgress: TaskProgress | null = null;
let activityLog: ActivityLog | null = null;
let workspaceWhiteboard: WorkspaceWhiteboard | null = null;
let elapsed = 0;

async function boot(): Promise<void> {
  // ── Init auth (but don't block — homepage is public) ───────
  await authManager.init();

  // ── If redirected from office with ?login=1 and already authed, skip homepage ──
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('login') === '1' && authManager.isAuthenticated) {
    window.location.href = '/office.html';
    return;
  }

  const renderer = createRenderer();
  await detectQuality();
  initLoaders();
  sceneManager = new SceneManager();

  // ── Load homepage scene ──────────────────────────────────
  updateProgress(0.2);
  createHomepageScene();
  const hpScene = getHomepageScene();
  const hpCamera = getHomepageCamera();
  sceneManager.setHomepageScene(hpScene, hpCamera);

  // ── Load exterior (in background, ready for transition) ──
  updateProgress(0.5);
  const exteriorScene = createExteriorScene();
  sceneManager.setExteriorScene(exteriorScene);
  updateProgress(0.8);

  // Input manager (starts disabled for homepage)
  inputManager = new InputManager(
    sceneManager.getExteriorCamera(),
    exteriorScene,
    renderer.domElement,
  );
  inputManager.disable();

  // HUD + Chat (hidden during homepage)
  hud = new HUD();
  chatPanel = new ChatPanel();
  if (import.meta.env.DEV) new DevLog();
  setupEvents();

  // Interior loading callback
  sceneManager.setOnInteriorNeeded(async () => {
    await loadInterior(renderer);
  });

  // ── State change handler ─────────────────────────────────
  sceneManager.setOnStateChange((state) => {
    if (state === 'HOMEPAGE') {
      // Show homepage overlay, enable scroll
      createHomepageOverlay();
      document.body.style.overflow = 'auto';
      document.body.style.background = '#fff8f0';
      // Let clicks pass through the fixed canvas to the HTML overlay
      renderer.domElement.style.pointerEvents = 'none';

      // Set up GSAP ScrollTrigger after overlay is in DOM
      requestAnimationFrame(() => {
        setupScrollAnimations(hpCamera, getHomepageDeskStations(), getHomepageDeliverables());
      });

      hud.hide();
      chatPanel.hide();
      inputManager.disable();
    } else if (state === 'EXTERIOR') {
      // Ensure homepage is cleaned up
      document.body.style.overflow = 'hidden';
      document.body.style.background = '#1a1a2e';
      renderer.domElement.style.pointerEvents = 'auto';
      hud.hide();
      chatPanel.hide();
      inputManager.enable();
      inputManager.setCamera(sceneManager.getExteriorCamera());
      inputManager.setScene(exteriorScene);
      if (orbitControls) {
        orbitControls.enabled = false;
      }
    } else if (state === 'INTERIOR') {
      document.body.style.overflow = 'hidden';
      renderer.domElement.style.pointerEvents = 'auto';
      inputManager.enable();
      if (workerManager) {
        inputManager.setCamera(sceneManager.getInteriorCamera());
        inputManager.setScene(sceneManager.getActiveScene()!);
      }
      if (orbitControls) {
        orbitControls.enabled = true;
      }

      // Auto-trigger onboarding on first entry (or show normal HUD)
      if (onboardingOrchestrator && !onboardingOrchestrator.isComplete) {
        onboardingOrchestrator.start();
      } else {
        hud.show();
        chatPanel.show();
        // Load current task state onto whiteboard
        refreshWhiteboard();
      }
    }
  });

  // ── Complete loading → enter homepage ────────────────────
  updateProgress(1.0);
  await new Promise((resolve) => setTimeout(resolve, 300));
  hidePreloader();
  sceneManager.enterHomepage();

  window.addEventListener('resize', handleResize);
  frame();
}

async function loadInterior(renderer: THREE.WebGLRenderer): Promise<void> {
  const interiorScene = createInteriorScene();
  sceneManager.setInteriorScene(interiorScene);

  workerManager = new WorkerManager(interiorScene);

  // Restore additional workers from persisted data (desk 0 is created by default)
  // Only do this AFTER onboarding is complete — onboarding adds workers itself
  if (dataManager.initialized && dataManager.isOnboardingComplete && dataManager.workers.length > 1) {
    const sortedWorkers = [...dataManager.workers].sort((a, b) => a.desk_index - b.desk_index);
    for (const w of sortedWorkers) {
      if (w.desk_index > 0) {
        workerManager.addWorker();
      }
    }
    hud.setWorkerCount(workerManager.getWorkerCount());
  }

  // Create onboarding orchestrator
  onboardingOrchestrator = new OnboardingOrchestrator(workerManager, chatPanel, hud, interiorScene);

  // Create task effects (status icons), task progress card, and workspace whiteboard
  taskEffects = new OnboardingEffects(interiorScene);
  taskEffects.setWorkerManager(workerManager);
  taskProgress = new TaskProgress();
  activityLog = new ActivityLog();
  workspaceWhiteboard = new WorkspaceWhiteboard(interiorScene);

  // Set up OrbitControls for interior camera
  const intCamera = sceneManager.getInteriorCamera();
  orbitControls = new OrbitControls(intCamera, renderer.domElement);
  orbitControls.target.set(0, 1, 0);
  orbitControls.enableDamping = true;
  orbitControls.dampingFactor = 0.08;
  orbitControls.minDistance = 5;
  orbitControls.maxDistance = 30;
  orbitControls.minPolarAngle = 0.3;           // Don't go below floor
  orbitControls.maxPolarAngle = Math.PI / 2.2;  // Don't go under
  orbitControls.enablePan = true;
  orbitControls.panSpeed = 0.5;
  orbitControls.enabled = true;
  orbitControls.update();

  setupPostprocessing(interiorScene);
}

function setupPostprocessing(interiorScene: THREE.Scene): void {
  const renderer = getRenderer();
  const camera = sceneManager.getInteriorCamera();
  const quality = getQualityPreset();

  composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(interiorScene, camera));

  if (quality.bloom) {
    const bloomEffect = new BloomEffect({
      intensity: 0.4,
      luminanceThreshold: 0.6,
      luminanceSmoothing: 0.3,
      mipmapBlur: true,
    });
    const smaaEffect = new SMAAEffect();
    composer.addPass(new EffectPass(camera, bloomEffect, smaaEffect));
  }
}

function setupEvents(): void {
  setupWorkerInteraction();
  setupAlexPlanningFlow();
  setupTaskStateHandling();
  setupPlanApprovalFlow();
  setupPlanCompletionFlow();

  // ── Small inline listeners ───────────────────────────────
  window.addEventListener('exit-interior', () => {
    inputManager.disable();
    hud.hide();
    chatPanel.hide();
    if (orbitControls) orbitControls.enabled = false;
    sceneManager.startTransition('exit');
  });

  window.addEventListener('action-requested', ((e: CustomEvent) => {
    const { actionId, workerIndex } = e.detail;
    workerManager?.commandWorker(workerIndex, actionId);
  }) as EventListener);

  window.addEventListener('add-coworker', () => {
    if (!workerManager) return;
    const newWorker = workerManager.addWorker();
    if (newWorker) {
      hud.setWorkerCount(workerManager.getWorkerCount());
    }
  });

  window.addEventListener('worker-thinking', ((e: CustomEvent) => {
    const { workerId, thinking } = e.detail;
    workerManager?.setWorkerThinking(workerId, thinking);
  }) as EventListener);

  window.addEventListener('transition-start', () => {
    inputManager.disable();
  });

  window.addEventListener('transition-end', () => {
    inputManager.enable();
  });

  window.addEventListener('onboarding-complete', () => {
    console.log('[Main] Onboarding complete');
  });

  window.addEventListener('enter-from-homepage', () => {
    handleEnterFromHomepage();
  });
}

// ── Worker Interaction (hover, click) ────────────────────────
function setupWorkerInteraction(): void {
  window.addEventListener('object-hover', ((e: CustomEvent) => {
    const { object } = e.detail;
    if (object?.userData.action === 'select-worker' && !onboardingOrchestrator?.isActive) {
      workerManager?.highlightWorker(object.userData.deskIndex, true);
    }
  }) as EventListener);

  window.addEventListener('object-unhover', ((e: CustomEvent) => {
    const { object } = e.detail;
    if (object?.userData.action === 'select-worker') {
      workerManager?.highlightWorker(object.userData.deskIndex, false);
    }
  }) as EventListener);

  window.addEventListener('object-click', ((e: CustomEvent) => {
    if (e.detail.action === 'enter') {
      inputManager.disable();
      sceneManager.startTransition('enter');
    }
    if (e.detail.action === 'select-worker' && !onboardingOrchestrator?.isActive) {
      const deskIndex = e.detail.object.userData.deskIndex as number;
      chatPanel.openForWorker(deskIndex);
    }
  }) as EventListener);
}

// ── Alex Planning Flow (whiteboard → plan presentation) ──────
function setupAlexPlanningFlow(): void {
  let alexAtWhiteboard = false;
  let pendingPlanResponse: { taskPlan: import('@/data/types').TaskPlan } | null = null;

  // Step A: User sends message to Alex → walk to whiteboard
  window.addEventListener('chat-message-sent', ((e: CustomEvent) => {
    const { workerIndex } = e.detail as { workerId: string; workerIndex: number };
    if (workerIndex === 0 && workerManager) {
      taskEffects?.showStatusIcon(0, 'thinking');
      workspaceWhiteboard?.showBrainstorming();
      chatPanel.setInterceptPlan(true);
      alexAtWhiteboard = false;
      workerManager.walkWorkerToWhiteboard(0, () => {
        alexAtWhiteboard = true;
        if (pendingPlanResponse) {
          const pending = pendingPlanResponse;
          pendingPlanResponse = null;
          processAlexPlanResponse(pending.taskPlan);
        }
      });
    }
  }) as EventListener);

  // Step C: AI response received
  window.addEventListener('chat-response-received', ((e: CustomEvent) => {
    const detail = e.detail as {
      workerId: string;
      workerIndex: number;
      hasTaskPlan?: boolean;
      taskPlan?: import('@/data/types').TaskPlan | null;
    };

    if (detail.workerIndex !== 0 || !workerManager) return;

    taskEffects?.hideStatusIcon(0);
    workspaceWhiteboard?.clearBrainstorming();
    // DON'T reset interceptPlan here — wait until plan presentation is done
    // This prevents the race where a second response renders before the plan presentation

    workerManager.setWorkerTalking('worker-0', true);
    setTimeout(() => workerManager?.setWorkerTalking('worker-0', false), 3000);

    if (detail.hasTaskPlan && detail.taskPlan) {
      if (alexAtWhiteboard) {
        processAlexPlanResponse(detail.taskPlan);
      } else {
        pendingPlanResponse = { taskPlan: detail.taskPlan };
      }
    } else {
      // No plan — reset intercept and walk back
      chatPanel.setInterceptPlan(false);
      alexAtWhiteboard = false;
      workerManager.walkWorkerBackToDesk(0);
    }
  }) as EventListener);

  function processAlexPlanResponse(plan: import('@/data/types').TaskPlan): void {
    alexAtWhiteboard = false;
    if (!workerManager) return;
    workerManager.walkWorkerToFrontCenter(0, () => {
      chatPanel.addAlexPlanPresentation(plan);
      // NOW reset intercept — plan presentation is rendered
      chatPanel.setInterceptPlan(false);
    });
  }
}

// ── Task State Handling (SSE task-update → animations + icons) ──
function setupTaskStateHandling(): void {
  apiClient.onSSE('task-update', (event) => {
    const data = event.data as {
      taskId: string;
      workerId: string;
      description: string;
      taskType: string;
      status: string;
      statusText?: string;
      deliverableId?: string;
    };

    workerManager?.handleTaskUpdate(data.workerId, data.status, data.taskType);

    // Green active indicator
    const deskIdx = extractDeskIndex(data.workerId);
    if (data.status === 'in_progress') {
      workerManager?.setWorkerActive(data.workerId, true);
    } else if (data.status === 'completed' || data.status === 'failed') {
      workerManager?.setWorkerActive(data.workerId, false);
    }

    // Activity log
    if (activityLog && deskIdx !== null) {
      const wName = workerManager?.getWorker(deskIdx)?.displayName || `Worker ${deskIdx}`;
      if (data.status === 'in_progress') {
        const text = data.statusText
          || `Working on: ${data.description?.slice(0, 60) || data.taskType || 'task'}...`;
        activityLog.show(wName, text);
      } else if (data.status === 'completed') {
        activityLog.show(wName, 'Finished! ✅', 5000);
      } else if (data.status === 'failed') {
        activityLog.show(wName, 'Task failed ❌', 5000);
      }
    }

    // Status icons above worker
    if (deskIdx !== null) {
      if (data.status === 'in_progress') {
        taskEffects?.showStatusIcon(deskIdx, taskTypeToIcon(data.taskType));
      } else if (data.status === 'completed') {
        taskEffects?.showStatusIcon(deskIdx, 'check');
        setTimeout(() => { taskEffects?.hideStatusIcon(deskIdx); }, 3000);
        window.dispatchEvent(new CustomEvent('task-completed', { detail: data }));
      } else if (data.status === 'failed') {
        taskEffects?.showStatusIcon(deskIdx, 'error');
        setTimeout(() => { taskEffects?.hideStatusIcon(deskIdx); }, 3000);
      }
    }

    refreshWhiteboard();
  });
}

// ── Plan Approval & Delegation Flow ──────────────────────────
function setupPlanApprovalFlow(): void {
  window.addEventListener('plan-approved', ((e: CustomEvent) => {
    refreshWhiteboard();
    if (!workerManager || !e.detail?.tasks) return;

    const allTasks = e.detail.tasks as Array<{
      id: string; worker_id: string; description: string; task_type: string;
    }>;
    const planId = e.detail.planId as string;
    const summary = e.detail.summary as string || 'Task Plan';

    // Build desk-to-task mapping
    const deskToTasks = new Map<number, typeof allTasks>();
    const deskIndices: number[] = [];
    for (const t of allTasks) {
      const idx = extractDeskIndex(t.worker_id);
      if (idx !== null && idx > 0) {
        if (!deskToTasks.has(idx)) {
          deskToTasks.set(idx, []);
          deskIndices.push(idx);
        }
        deskToTasks.get(idx)!.push(t);
      }
    }

    chatPanel.addAlexDialogue("Excellent! Let me brief the team and get everyone started.");
    workerManager?.setWorkerTalking('worker-0', true);
    setTimeout(() => workerManager?.setWorkerTalking('worker-0', false), 2500);

    for (const idx of deskIndices) {
      taskEffects?.showStatusIcon(idx, 'pending');
    }

    taskProgress?.initEmpty(planId, summary);

    setTimeout(() => {
      if (deskIndices.length > 0 && workerManager) {
        workerManager.delegateToWorkers(
          deskIndices,
          (deskIndex) => {
            workerManager?.highlightWorker(deskIndex, true);
            setTimeout(() => workerManager?.highlightWorker(deskIndex, false), 800);
            const workerTasks = deskToTasks.get(deskIndex) || [];
            for (const t of workerTasks) {
              taskProgress?.addTask(t);
            }
          },
          () => {
            console.log('[Main] Boss delegation complete');
          },
          2000,
        );
      }
    }, 1200);
  }) as EventListener);
}

// ── Plan Completion (celebration + deliverable presentation) ──
function setupPlanCompletionFlow(): void {
  apiClient.onSSE('plan-completed', (event) => {
    const data = event.data as {
      planId: string;
      deliverableId: string | null;
      totalTasks: number;
      completedTasks: number;
    };
    console.log(`[Main] Plan completed: ${data.completedTasks}/${data.totalTasks} tasks`, data);

    window.dispatchEvent(new CustomEvent('plan-completed', { detail: data }));
    refreshWhiteboard();
    activityLog?.hide();

    // Stop all working animations
    if (workerManager) {
      const wCount = workerManager.getWorkerCount();
      for (let i = 0; i < wCount; i++) {
        workerManager.stopWorkingAnimation(i);
      }
    }

    workerManager?.celebrateAll(() => {
      if (!workerManager) return;

      // Determine the presenting worker (first non-Alex worker, or Alex if solo)
      const wCount = workerManager.getWorkerCount();
      const presenterIndex = wCount > 1 ? 1 : 0; // First non-boss worker

      // Non-presenter workers walk back to desks
      for (let i = 0; i < wCount; i++) {
        if (i !== presenterIndex) {
          workerManager.walkWorkerBackToDesk(i);
        }
      }

      // Presenter walks to front-center
      workerManager.walkWorkerToFrontCenter(presenterIndex, () => {
        const presenter = workerManager?.getWorker(presenterIndex);
        const presenterName = presenter?.displayName || 'Worker';

        // Open chat for the presenting worker
        chatPanel.openForWorker(presenterIndex);

        // Presenter dialogue
        if (data.completedTasks > 0) {
          chatPanel.addAlexDialogue(
            `Here's the report you asked for, boss! Take a look and let me know if you want any changes. 📋`,
          );
          workerManager?.setWorkerTalking(`worker-${presenterIndex}`, true);
          setTimeout(() => workerManager?.setWorkerTalking(`worker-${presenterIndex}`, false), 3000);

          // Load and show deliverable card
          if (data.deliverableId) {
            loadAndShowDeliverable(data.deliverableId);
          }
        } else {
          // All tasks failed
          chatPanel.openForWorker(0); // Alex presents failure
          chatPanel.addAlexDialogue(
            "Sorry boss, the team ran into some issues and couldn't complete the tasks. Want to try again with a different approach?",
          );
          workerManager?.setWorkerTalking('worker-0', true);
          setTimeout(() => workerManager?.setWorkerTalking('worker-0', false), 3000);
        }

      });
    });
  });
}

/** Gate auth + load data, then transition from homepage to exterior */
async function handleEnterFromHomepage(): Promise<void> {
  console.log('[Boot] handleEnterFromHomepage called');

  try {
    // ── Auth gate — only required when entering the office ────
    if (!authManager.isAuthenticated) {
      console.log('[Boot] Not authenticated — showing login screen');
      showLoginScreen();
      await new Promise<void>((resolve) => {
        let resolved = false;
        const done = () => {
          if (resolved) return;
          resolved = true;
          hideLoginScreen();
          resolve();
        };
        authManager.onAuthChange((session) => {
          if (session || authManager.isDevMode) done();
        });
        if (authManager.isDevMode) done();
      });
      console.log('[Boot] Auth resolved');
    } else {
      console.log('[Boot] Already authenticated (devMode:', authManager.isDevMode, ')');
    }

    // ── Redirect to office page ────────────────────────────────
    console.log('[Boot] Auth complete — redirecting to office');
    window.location.href = '/office.html';
  } catch (err) {
    console.error('[Boot] handleEnterFromHomepage failed:', err);
    // Re-enable the button so user can try again
    const btn = document.getElementById('hp-enter-btn') as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Deploy Your Hatchlings Now';
    }
  }
}

/** Map task_type to a status icon type */
function taskTypeToIcon(taskType: string): StatusIconType {
  switch (taskType) {
    case 'research': return 'search';
    case 'analyze': return 'analyze';
    case 'draft': return 'document';
    case 'plan': return 'document';
    default: return 'search';
  }
}

/** Load a deliverable from the API and show it as a card in chat */
async function loadAndShowDeliverable(deliverableId: string): Promise<void> {
  try {
    const deliverable = await dataManager.getDeliverableContent(deliverableId);
    if (deliverable) {
      chatPanel.addDeliverableCard(deliverable);
    }
  } catch (err) {
    console.warn('[Main] Failed to load deliverable for presentation:', err);
  }
}

/** Refresh the workspace whiteboard with current tasks from backend */
async function refreshWhiteboard(): Promise<void> {
  if (!workspaceWhiteboard) return;
  try {
    const tasks = await dataManager.listTasks();
    workspaceWhiteboard.syncTasks(tasks);
  } catch (err) {
    console.debug('[Main] Whiteboard refresh failed:', err);
  }
}

function frame(): void {
  requestAnimationFrame(frame);

  const delta = appClock.getDelta();
  elapsed += delta;

  sceneManager.update(delta);
  workerManager?.update(delta);
  onboardingOrchestrator?.update(delta);
  taskEffects?.update(delta);

  // Animate homepage diorama
  if (sceneManager.currentState === 'HOMEPAGE') {
    updateHomepageScene(elapsed);
  }

  // Animate exterior environment (ducks, birds, clouds)
  if (sceneManager.currentState === 'EXTERIOR' || sceneManager.currentState === 'LOADING') {
    updateExteriorScene(elapsed);
  }

  // Update orbit controls
  if (orbitControls?.enabled) {
    orbitControls.update();
  }

  const activeCamera = sceneManager.getActiveCamera();
  const activeScene = sceneManager.getActiveScene();
  if (!activeScene) return;

  if (sceneManager.currentState === 'INTERIOR' && composer) {
    composer.render(delta);
  } else {
    const renderer = getRenderer();
    renderer.render(activeScene, activeCamera);
  }
}

function handleResize(): void {
  const renderer = getRenderer();
  renderer.setSize(window.innerWidth, window.innerHeight);
  sceneManager.handleResize();
  resizeHomepage();
  composer?.setSize(window.innerWidth, window.innerHeight);
}

boot().catch((err) => {
  console.error('Failed to boot Hatchlings:', err);
});
